// React
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Divider } from "@mui/material";
// Others
import { useSnackbar } from "notistack";
import EmployeeTrxConfig from "../component/EmployeeTrxConfig";
import EmployeeAdvancedConfig from "../component/EmployeeAdvancedConfig";
import EmployeeDualBrandCardConfig from "../component/EmployeeDualBrandCardConfig";
import EmployeeOptionConfig from "../component/EmployeeOptionConfig";
import EmployeeFunctionAccControlConfig from "../component/EmployeeFunctionAccControlConfig";
import EmployeeDeveloperConfig from "../component/EmployeeDeveloperConfig";
import EmployeeKioskOption from "../component/EmployeeKioskOption";

// Model
import { updateEmployeeConfig } from "../model";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { formPreventEnterSubmit } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import useFormUndo from "utils/useFormUndo/useFormUndo";

const EMPLOYEE_LOCALE_KEY = "page.employees.";

/**
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<import("../OfflineEmployeeDetail").offlineEmployeeDetailPartitionKeyType>} props.callApis 
 * @returns 
 */
const EmployeeConfig = ({ callApis, employeeConfig, fetchEmployeeConfig }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps;
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });
  const { PageStickyFooter } = usePageWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useEffect(() => {
    reset(employeeConfigSpecialHandling(employeeConfig, "fetch"));
  }, [employeeConfig]);

  const onSubmit = async (formValue) => {
    try {
      setIsLoading(true);
      const finalFormValue = employeeConfigSpecialHandling(formValue, "update");
      const [updateEmployeeConfigResp] = await callApis([{
        api: updateEmployeeConfig,
        payload: finalFormValue,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      if (updateEmployeeConfigResp.isSuccess) {
        snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess")}`,
          "success",
        );
        await initialFetch();
        setIsEditing(false);
        clearFormMemory();
      } else {
        updateEmployeeConfigResp.isError && snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const buttons = () => (
    <Stack direction="row" justifyContent="flex-end" gap={2}>
      {!isEditing ? (
        <ButtonMAS
          variant="primary"
          type="button"
          onClick={() => {
            setIsEditing(true);
            rememberForm();
          }}
        >
          {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.edit`)}
        </ButtonMAS>
      ) : (
        <>
          <ButtonMAS
            variant="bordered"
            onClick={() => {
              setIsEditing(false);
              undoForm();
            }}
            type="button"
            disabled={isLoading}
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.cancel`)}
          </ButtonMAS>
          <ButtonMAS
            variant="primary"
            type="submit"
            isLoading={isLoading}
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.save`)}
          </ButtonMAS>
        </>
      )}
    </Stack>
  );

  const ACCESS_CONTROL_MAPPINGS = {
    Void: "accessControlVoid",
    Refund: "accessControlRefund",
    Auth: "accessControlAuthorization",
    Completion: "accessControlAuthComplete",
    History: "accessControlHistory",
    Settlement: "accessControlSettlement",
    Settings: "accessControlSetting",
    AuthTopUp: "accessControlAuthTopUp",
    TipsAdj: "accessControlTipsAdjustment",
    ScreenSaver: "accessControlScreenSaver",
  };

  const CARD_TYPE_MAPPINGS = {
    visa: "dualBranchEnableVisa",
    master: "dualBranchEnableMaster",
    amex: "dualBranchEnableAmex",
    jcb: "dualBranchEnableJcb",
  };

  const employeeConfigSpecialHandling = (employeeConfig, type) => {
    if (type === "fetch") {
      const config = { ...employeeConfig };

      // Handle param "optDualBrandCardType"
      if (config.optDualBrandCardType) {
        const cardTypes = config.optDualBrandCardType.toLowerCase().split(",");
        cardTypes.forEach((type) => {
          if (CARD_TYPE_MAPPINGS[type]) {
            config[CARD_TYPE_MAPPINGS[type]] = true;
          }
        });
      }

      // Handle param "opPassword"
      if (!config.opPassword) return config;

      const opPasswordObj = config.opPassword.split(",").reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        if (key && value) acc[key] = value;
        return acc;
      }, {});

      Object.entries(ACCESS_CONTROL_MAPPINGS).forEach(([shortKey, fullKey]) => {
        if (opPasswordObj[shortKey] !== undefined) {
          config[fullKey] = opPasswordObj[shortKey];
        }
      });

      return config;
    }
    if (type === "update") {
      const formValue = JSON.parse(JSON.stringify(employeeConfig));

      // Map access control fields
      Object.entries(ACCESS_CONTROL_MAPPINGS).forEach(([shortKey, fullKey]) => {
        if (formValue[fullKey] !== undefined) {
          formValue[shortKey] = formValue[fullKey];
        }
      });

      // Build opPassword string
      formValue.opPassword = Object.entries(ACCESS_CONTROL_MAPPINGS)
        .reduce((acc, [shortKey, fullKey]) => {
          if (formValue[fullKey] !== undefined) {
            acc.push(`${shortKey}=${formValue[fullKey]}`);
            delete formValue[shortKey];
            delete formValue[fullKey];
          }
          return acc;
        }, [])
        .join(",");

      // Handle "optDualBrandCardType"
      formValue.optDualBrandCardType = Object.entries(CARD_TYPE_MAPPINGS)
        .filter(([, value]) => {
          const isThatCardTypeEnabled = formValue[value];
          delete formValue[value];
          return isThatCardTypeEnabled;
        })
        .map(([key]) => key)
        .join(",");

      formValue.staticQrEnabledList = JSON.parse(
        formValue?.staticQrEnabledList ?? "[]",
      );
      formValue.optDcc = !!formValue?.opt_dcc;
      formValue.optDccSingapore = !!formValue?.opt_dcc_singapore;
      formValue.optIpp = !!formValue?.opt_ipp;
      formValue.optUnlinkRefund = !!formValue?.opt_unlink_refund;
      return formValue;
    }

    return employeeConfig;
  };

  const initialFetch = async () => {
    try {
      await fetchEmployeeConfig();
    } catch (error) {
      console.error("Error fetching employee detail:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchEmployeeConfigFail")}`,
        "error",
      );
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
      <FormBuilder.Section>
        <EmployeeTrxConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeAdvancedConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeDualBrandCardConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeOptionConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeFunctionAccControlConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeDeveloperConfig
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <EmployeeKioskOption
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
        />
        <PageStickyFooter>
          {buttons()}
        </PageStickyFooter>
      </FormBuilder.Section>
    </form>
  );
};

export default React.memo(EmployeeConfig);
